import type { RepetitionDaysOfWeek } from 'containers/ResourcePlanning/Scheduler/components/Dialogs/SinglePlanDialog/utils';

import type { EmployeeAndSubcontractorUser, UserTag } from 'model/User/types';

import type { ProjectTask } from '../Projects/types';

export enum PlanCustomPropertyType {
  Dropdown = 0,
  Dataset = 1,
  Text = 2,
}

export interface PlanCustomPropertyOption {
  id: string;
  name: string;
}

export interface PlanCustomPropertyDefinition {
  id: string;
  name: string;
  type: PlanCustomPropertyType;
  datasetId: string | null;
  options: PlanCustomPropertyOption[];
  sortOrder: number;
  isActive: boolean;
}

export interface PlanCustomPropertyValue {
  propertyId: string;
  value: string | null;
  dropdownIds: string[];
  datasetItemName?: string | null;
}

export interface PlanningCompanySettingsResponse {
  id: string;
  companyId: string;
  customProperties: PlanCustomPropertyDefinition[];
}

export interface MachineForPlanning {
  companyId: string;
  machineId: string;
  machineName: string;
  machineNumber: string;
  plans: {
    [date: string]: MachineDayPlan;
  };
}

export interface BasePlan {
  comment: string | null;
  companyId: string;
  companyIds: string[];
  /** ISO date string */
  createdAt: string;
  createdByUserId: string;
  /** yyyy-MM-ddT00:00:00Z */
  date: string;
  departmentCode: string;
  id: string | null;
  isChapter: boolean;
  /** ISO date string */
  modifiedAt: string;
  modifiedByUserId: string;
  projectId: string;
  projectName: string;
  moveReason: string | null;
  moveCategory: string | null;
  moveCause: string | null;
  recVersion: number;
  refId: string | null;
  taskId: string;
  taskName: string;
  taskNumber: string | null;
  customProperties?: PlanCustomPropertyValue[];
}

export interface UserPlan extends BasePlan {
  defaultResourceId: string;
  isAutoPlanned: boolean;
  isUserOnLoan: boolean;
  machineIds: string[];
  roles: string[];
  userId: string;
  workDescription: string | null;
}

export interface MachinePlan extends BasePlan {
  isMachineOnLoan: boolean;
  machineId: string;
  machineName: string;
  machineNumber: string | null;
}

export const isUserPlanGuard = (
  plan: UserPlan | MachinePlan
): plan is UserPlan => !!(plan as UserPlan).userId;

export interface DayPlan {
  date: string;
  projectPlans: UserPlan[];
  shifts: PlanningShift[];
}

export interface UserForPlanning {
  userId: string;
  userName: string;
  companyId: string;
  plans: {
    [date: string]: DayPlan;
  };
  userResources: string[] | null;
  userTags: UserTag[];
}

export interface RepeatedPlanPayload
  extends Pick<BasePlan, 'moveCategory' | 'moveCause' | 'moveReason'> {
  companyId: string;
  projectId: string;
  taskId: string;
  userId: string;
  userName: string;
  comment: string | null;
  daysOfWeek: RepetitionDaysOfWeek[];
  /** Array of ISO date strings */
  dates: string[];
  /** ISO date string */
  startDate: string;
  /** ISO date string */
  endDate: string;
  machineIds: string[];
  roles: string[];
  customProperties?: PlanCustomPropertyValue[];
  // For placeholder card only
  taskName: UserPlan['taskName'];
  taskNumber: UserPlan['taskNumber'];
}

export interface RepeatedMachinePlanPayload
  extends Pick<BasePlan, 'moveCategory' | 'moveCause' | 'moveReason'> {
  companyId: string;
  projectId: string;
  taskId: string;
  machineId: string;
  machineName: string;
  comment: string | null;
  daysOfWeek: RepetitionDaysOfWeek[];
  /** Array of ISO date strings */
  dates: string[];
  /** ISO date string */
  startDate: string;
  /** ISO date string */
  endDate: string;
  customProperties?: PlanCustomPropertyValue[];
  // For placeholder card only
  taskName: UserPlan['taskName'];
  taskNumber: UserPlan['taskNumber'];
}

export interface MachineDayPlan {
  date: string;
  projectPlans: MachinePlan[];
}

export interface MachinePlanningTask {
  comment: string;
  companyId: string;
  companyIds?: string[];
  createdAt?: string;
  createdByUserId?: string;
  date: string;
  id: string;
  isChapter?: boolean;
  isMachineOnLoan?: boolean;
  machineId: string;
  machineName?: string;
  machineNumber?: string;
  modifiedAt?: string;
  modifiedByUserId?: string;
  projectId: string;
  projectName?: string;
  recVersion?: number;
  taskId: string;
  taskName?: string;
  taskNumber?: string;
}

export type UserOrMachinePlans = (UserPlan | MachinePlan)[];

export const isUserForPlanningArray = (
  plan: (UserForPlanning | MachineForPlanning)[]
): plan is UserForPlanning[] =>
  plan.length > 0 && !!(plan[0] as UserForPlanning).userId;

interface ShiftAuxData {
  fromDateTime: string;
  toDateTime: string;
  shiftDescription: string;
}

export interface PlanningShift {
  auxData: null | ShiftAuxData[];
  id: string;
  colorCode: string;
  date: string;
  fromDateTime: string;
  isAbsence: boolean;
  isPartialAbsence: boolean;
  isContingencyShift: boolean;
  shiftCategory: string;
  shiftCode: string;
  shiftDescription: string;
  shiftRemark: string;
  toDateTime: string;
  userId: string;
  workDescription: string;
  workHours: number;
}

export interface DraggedItem {
  draggedPlan: UserPlan | MachinePlan;
}

export enum TaskCardStatus {
  Placeholder,
  Draggable,
  InProgress,
  ClickAndChoose,
  ClickAndDrag,
  Absence,
  Contingency,
  OnLoan,
}

export type NewTask = {
  id?: null;
  comment: string | null;
  companyId: string;
  projectId: string;
  taskId: string;
  date: string;
  userId: string;
  machineIds: string[] | null;
  roles: string[] | null;
  customProperties?: PlanCustomPropertyValue[];
  // For placeholder card only
  taskName: string;
  taskNumber: string | null;
  moveReason: string | null;
  moveCategory: string | null;
  moveCause: string | null;
};

export type NewMachineTask = {
  id?: null;
  comment: string | null;
  companyId: string;
  projectId: string;
  taskId: string;
  date: string;
  machineId: string;
  machineIds?: never;
  roles?: never;
  customProperties?: PlanCustomPropertyValue[];
  // For placeholder card only
  taskName: string;
  taskNumber: string | null;
  moveReason: string | null;
  moveCategory: string | null;
  moveCause: string | null;
};

export enum TaskCardTooltipStatus {
  ClickCardToReplace,
  Add,
  ReplaceWith,
  Delete,
}

export interface Role {
  id: string;
  companyId: string;
  companyIds: string[];
  name: string;
  nameNormalized: string;
  description: string;
}

export interface PlannerResource {
  active: boolean;
  avatarImageId: string;
  department: string | null;
  id: string;
  resourceName: string;
  resourceNameFull: string;
  resourceNumber: string | null;
  type: PlannerResourceType;
}

interface PlannerResourceType {
  baseType: ResourceBaseType;
  companyId: string;
  companyIds: string[];
  description: string;
  id: string;
  isDumper: boolean;
  isLoader: boolean;
  isRoadTruck: boolean;
  recVersion: number;
  typeId: string;
}

export enum ResourceBaseType {
  Human = 0,
  Machine = 5,
  Vehicle = 10,
  SeaVessel = 15,
  AirVessel = 20,
  StaticObject = 25,
  Other = 100,
}

export interface UsersResources {
  baseType: ResourceBaseType;
  id: string;
  resourceNameFull: string;
  resourceNumber: null | string;
}

export interface AssignedResources {
  item1: {
    [userId: string]: UsersResources[];
  };
  item2: {
    [machineId: string]: string[];
  };
}

export const isUserForPlanning = (
  plan: UserForPlanning | MachineForPlanning
): plan is UserForPlanning => !!(plan as UserForPlanning).userId;

export const isUserForPopover = (
  entity: EmployeeAndSubcontractorUser | PlannerResource
): entity is EmployeeAndSubcontractorUser =>
  !!(entity as EmployeeAndSubcontractorUser).identityUserId;

export enum UserSortType {
  Alphabetical,
  Custom = 10,
}

export interface CertificateOrTagOption {
  id: string;
  isTag: boolean;
  name: string;
}

export interface NewInternalNote {
  companyId: string;
  date: Date;
  projectId: string;
  userId: string | null;
  machineId: string | null;
  comment: string;
}

export interface InternalNote extends Omit<NewInternalNote, 'date'> {
  id: string;
  recVersion: number;
  companyIds: ['string'];
  createdByUserId: string;
  createdDateTime: string;
  createdByUserName: string;
  createdByUserAvatarSrc: string;
  date: string;
}

interface FilterTask {
  id: string;
  taskName: string;
  taskNumber: string;
  projectId: string;
  isChapter: string;
}

export interface FiltersForPlanning {
  machines: unknown;
  tasks: { [taskId: string]: FilterTask };
}

export interface RecentPlan {
  id: string;
  projectId: string;
  projectName: string;
  task: ProjectTask;
}

export interface NewPlanningSettings {
  id?: string;
  userId: string;
  settings: {
    projectId?: string;
    projectIds?: string[];
    showTaskNumber?: boolean;
    showRoles?: boolean;
    showResources?: boolean;
    showComments?: boolean;
    showEmployeeNumbers?: boolean;
    onlyShowUsersLackingPlans?: boolean;
    visibleCustomPropertyIds?: string[];
    recentPlans?: RecentPlan[];
  };
}

export interface PlanningSettings extends Required<NewPlanningSettings> {
  companyId: string;
  collectionRef: 'PlannerUserSettingsDS';
}

export interface CompactUserPlans {
  [date: string]: {
    plans: UserPlan[];
    shifts: PlanningShift[];
  };
}

export interface SpanningShifts {
  [shiftKey: string]: { start: number; end: number; weight: number };
}
